import { useState, useEffect, useRef } from 'react';
import classes from './index.module.scss';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  assessmentMessages,
  generalErrorMessage,
} from '../../constants/messages';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { authStatusEnum, setStatus } from '../../store/auth/authSlice';
import { assessmentSubmission } from '../../services/api/assessment';
import { useParams } from 'react-router-dom';
import { CustomSnackbarProps } from '../CustomSnackbar';
import { useAppSelector } from '../../hooks/useAppSelector';
import { setToastState } from '../../store/toast/toastSlice';
import { ExitFullScreen } from '../../utils/helper';
import { tokenKey } from '../../utils/apiClient/contant';
import { setPhotoUploadPause } from '../../store/assessment/assessmentSlice';
import CircularProgress from '@mui/material/CircularProgress';
import {
  setMarkAsUnattempted,
  setWaitingForUnAttemptedMark,
} from '../../store/time/timeSlice';
import { questionType } from '../../store/assessment/questionSlice';

interface SubmitAssessmentDialogProps {
  handleClose: () => void;
  open: boolean;
}

const SubmitAssessmentDialog = ({
  handleClose,
  open,
}: SubmitAssessmentDialogProps) => {
  const { accessKey } = useParams();
  const dispatch = useAppDispatch();
  const assessmentState = useAppSelector((state) => state.assessment);
  const toastState = useAppSelector((state) => state.toast);
  const { waitingForUnAttemptedMark, shouldMarkUnattempted, videoRecording } =
    useAppSelector((state) => state.time);
  const inCompleteVideoQuestion = videoRecording;
  const [pause, setPause] = useState(false);
  const [waitingMark, setWaitingMark] = useState(false);
  const submitInFlight = useRef(false);

  const openSnackbar = (
    type: CustomSnackbarProps['type'],
    message: CustomSnackbarProps['message']
  ) => {
    dispatch(
      setToastState({
        ...toastState,
        type,
        open: true,
        message,
      })
    );
  };

  const handleSubmitConfirm = () => {
    setPause(true);
    dispatch(setPhotoUploadPause(true));
    if (inCompleteVideoQuestion) {
      dispatch(setWaitingForUnAttemptedMark(true));
      dispatch(setMarkAsUnattempted(true));
      setWaitingMark(true);
    }
  };

  const handleSubmitLog = async () => {
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (submitInFlight.current) return;

    submitInFlight.current = true;
    setWaitingMark(false);
    let submissionSucceeded = false;
    try {
      // await assessmentLog(accessKey as string, LogEvents.EYE_VIEW_DEVIATION, {
      //   deviation_time: totalDeviationForMinute,
      // });
      const response = await assessmentSubmission(accessKey as string);
      if (response) {
        submissionSucceeded = true;
        dispatch(setStatus(authStatusEnum.ENDED));
        sessionStorage.removeItem(tokenKey);
        ExitFullScreen();
        const { success, message } = response.data;
      }
    } catch (err) {
      console.error(err);
      openSnackbar('error', generalErrorMessage);
    } finally {
      if (!submissionSucceeded) {
        submitInFlight.current = false;
        setPause(false);
      }
    }
  };

  useEffect(() => {
    if (pause && !assessmentState.photo_upload_pause) {
      if (!inCompleteVideoQuestion) {
        handleSubmitLog();
      } else if (
        !shouldMarkUnattempted &&
        !waitingForUnAttemptedMark &&
        waitingMark
      ) {
        handleSubmitLog();
      }
    }
  }, [
    pause,
    assessmentState.photo_upload_pause,
    inCompleteVideoQuestion,
    shouldMarkUnattempted,
    waitingForUnAttemptedMark,
  ]);

  useEffect(() => {
    if (!open) {
      setPause(false);
      dispatch(setPhotoUploadPause(false));
    }
  }, [open]);

  const handleLocalClose = () => {
    dispatch(setMarkAsUnattempted(false));
    dispatch(setWaitingForUnAttemptedMark(false));
    handleClose();
  };

  return (
    <Dialog
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            maxWidth: '500px',
            borderRadius: '8px',
          },
        },
        backdropFilter: 'blur(0.2rem)',
      }}
      onClose={handleLocalClose}
      open={open}
    >
      <DialogTitle>
        <div className={classes.dialogTitle}>
          <div>
            {inCompleteVideoQuestion
              ? 'Incomplete Submission Alert!'
              : 'Submit Assessment'}
          </div>
          {inCompleteVideoQuestion ? (
            <></>
          ) : (
            <div>
              {/* <IconButton onClick={handleLocalClose}>
                <ClearIcon />
              </IconButton> */}
            </div>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.dialogContent}>
          {inCompleteVideoQuestion
            ? assessmentMessages.incompleteSubmissionMessage
            : assessmentMessages.submitMessage}
        </div>
        <div className={classes.assessmentDetailContainer}>
          <div className={classes.assessmentDetail}>
            <div>Total Questions</div>
            <div className={classes.infoChip}>
              {assessmentState.total_question}
            </div>
          </div>
          <div className={classes.assessmentDetail}>
            <div>Question Attempted</div>
            <div className={classes.successChip}>
              {assessmentState.question_answered}
            </div>
          </div>
          <div className={classes.assessmentDetail}>
            <div>Question Unattempted</div>
            <div className={classes.warningChip}>
              {assessmentState.total_question -
                assessmentState.question_answered}
            </div>
          </div>
        </div>
        {inCompleteVideoQuestion ? (
          <div style={{ padding: '8px 0' }}>What would you like to do?</div>
        ) : (
          <></>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '24px' }}>
        <button className={classes.noButton} onClick={handleLocalClose}>
          {assessmentState.questions_map[
            assessmentState.current_question_number - 1
          ]?.question_type === questionType?.SUBJECTIVE
            ? 'STAY ON VIDEO QUESTION'
            : 'NO'}
        </button>
        <button
          className={classes.yesButton}
          disabled={pause}
          onClick={handleSubmitConfirm}
        >
          {pause ? (
            <CircularProgress
              style={{ color: 'white', height: '14px', width: '14px' }}
            />
          ) : assessmentState.questions_map[
              assessmentState.current_question_number - 1
            ]?.question_type === questionType?.SUBJECTIVE ? (
            'SUBMIT ASSESSMENT ANYWAY'
          ) : (
            'YES, SUBMIT'
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitAssessmentDialog;
